import React from 'react';
import {ThemeProvider as MuiThemeProvider, Theme as MuiTheme} from '@mui/material/styles';
import {jdGreen} from '../themes';
import * as jdGreenTokens from '@deere/fuel-tokens/dist/web/jd-green/tokens';
import * as jdBlackTokens from '@deere/fuel-tokens/dist/web/jd-black/tokens';
import type {ThemeProviderProps as MuiThemeProviderProps} from '@mui/material/styles/ThemeProvider';
import {GlobalStyles} from '../GlobalStyles/GlobalStyles';
import {CssBaseline} from '../CssBaseline';

export type Tokens = {
    [Key in keyof (typeof jdGreenTokens | typeof jdBlackTokens)]: typeof jdGreenTokens[Key] | typeof jdBlackTokens[Key];
};

declare module '@mui/material/styles/createTheme' {
    interface Theme {
        name: 'jdBlack' | 'jdGreen';
        tokens: Tokens;
    }

    interface ThemeOptions {
        name: 'jdBlack' | 'jdGreen';
        tokens: Tokens;
    }
}

/*
    Exporting the overriden theme from @mui/material
*/
export type Theme = MuiTheme;

export interface ThemeProviderProps extends Omit<MuiThemeProviderProps,
'theme'
> {
    /*
      Include CSS Baseline which brings in global CSS to application.
    */
    baseline?: boolean;
    /*
      Include font files
    */
    includeFonts?: boolean;
    /**
     * @default jdGreen
     */
    theme?: Partial<MuiTheme>;
}

/**
 * Read more about why these value are stored in the mui docs:
 * @link https://mui.com/material-ui/customization/how-to-customize/#4-global-css-override
 */
const globalStyles = <GlobalStyles/>;
const globalStylesWithoutFonts = <GlobalStyles excludeFontStyles/>;

export const ThemeProvider = (
    {
        children,
        baseline = true,
        includeFonts = true,
        theme = jdGreen,
        ...props
    }: ThemeProviderProps): React.ReactElement => {
    return (
        <MuiThemeProvider
            theme={theme}
            {...props}
        >
            <div data-theme={theme.name === 'jdBlack' ? 'fuel-jd-black' : 'fuel-jd-green'}>
                {baseline && <CssBaseline/>}
                {includeFonts ? globalStyles : globalStylesWithoutFonts}
                {children}
            </div>
        </MuiThemeProvider>
    );
};
